import {
  type ShiftBreakRecord,
  type ShiftBreak
} from '@libs/data-access/queries'
import { Flex, Text } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { usePadSettings } from '@spa/scenes/Plannings/hooks'
import React from 'react'
import type { FC } from 'react'

import { displayRules, type CardType } from '../../utils'

const BREAK_EVENT = 'break_event'

type Props = {
  breaks: ShiftBreakRecord[] | ShiftBreak[]
  size: number
  type: 'planned' | 'real'
  cardType?: CardType
  shiftIsFinishedWithoutOut: boolean
  hasEmployeeModifyReal?: boolean
  isShiftValid?: boolean
}
export const BreakCol: FC<Props> = ({
  breaks,
  size,
  type = 'planned',
  cardType = 'planned',
  shiftIsFinishedWithoutOut,
  hasEmployeeModifyReal = false,
  isShiftValid = false
}) => {
  const { t, tCommon, tDates } = usePlanningFormTranslation()

  const { hasSnapPad, padType, hasSnapPadBreaksEnable, breakAsReal } =
    usePadSettings()

  const noFinishedBreaks =
    ((cardType === 'pad' && hasSnapPad && hasSnapPadBreaksEnable) ||
      cardType === 'real') &&
    breaks?.length === 0

  /* If no break is taken then we display a single line */
  if (noFinishedBreaks) {
    return (
      <Flex row className='gap-1'>
        <Text as='span' className='text-neutral-500'>
          {t('shiftRealHoursForm.breakCol.break', { count: size })}
        </Text>
        <span className='text-left text-semantic-orange-700'>
          {padType === BREAK_EVENT
            ? tCommon('emptyState.none')
            : t('shiftRealHoursForm.breakCol.emptyMinute', { duration: 0 })}
        </span>
      </Flex>
    )
  }

  return (
    <Flex row>
      <Text as='span' className='text-neutral-500'>
        {t('shiftRealHoursForm.breakCol.break', { count: size })}
      </Text>
      <Flex>
        {breaks?.map((shiftBreak, index) => {
          const plannedDuration = shiftBreak.planned_duration
          const realDuration = shiftBreak.real_duration
          /* If we're watching planned break and plannedDuration === 0
             This break wasn't created by a shift creation but with snap-pad */
          if (type === 'planned' && plannedDuration === 0) {
            return null
          }

          const typedStart = `${type}_starts_at` as
            | 'planned_starts_at'
            | 'real_starts_at'
          const typedEnd = `${type}_ends_at` as
            | 'planned_ends_at'
            | 'real_ends_at'
          const typeDuration = `${type}_duration` as
            | 'planned_duration'
            | 'real_duration'

          const isFirst = index === 0
          const caseDuration = displayRules(
            shiftBreak.id,
            breakAsReal,
            shiftBreak.shift_event_ids?.length,
            shiftBreak.planned_duration,
            shiftIsFinishedWithoutOut,
            shiftBreak[typedStart],
            cardType,
            isFirst,
            shiftBreak[typeDuration],
            shiftBreak[typedEnd],
            false,
            hasSnapPad
          )

          const isWarningStyle =
            (type === 'real' && realDuration > plannedDuration) || caseDuration

          const nullDuration =
            plannedDuration > 0 &&
            shiftBreak[typeDuration] === 0 &&
            cardType === 'real' &&
            !hasEmployeeModifyReal &&
            breakAsReal &&
            breaks.length === 0 &&
            hasSnapPad &&
            hasSnapPadBreaksEnable &&
            '0 min'

          /* If nullDuration exists, the duration shouldn't exist so duration = '' */
          const rangeDates = tDates('range.shortHourMinuteDashed', {
            start: shiftBreak[typedStart],
            end: shiftBreak[typedEnd]
          })

          const duration =
            !nullDuration &&
            (type === 'planned' ||
              breaks.length === 2 ||
              hasEmployeeModifyReal ||
              isShiftValid ||
              (shiftBreak.event_type === 'break_duration' &&
                breaks.length === 1)) &&
            t('shiftRealHoursForm.breakCol.duration', {
              duration: shiftBreak[typeDuration],
              range: shiftBreak[typedStart] ? `(${rangeDates})` : ''
            })

          return (
            <div
              key={index}
              className={`!ml-1 text-left ${isWarningStyle ? 'text-semantic-orange-700' : 'text-neutral-500'}`}
            >
              {!caseDuration && nullDuration}
              {caseDuration || duration}
            </div>
          )
        })}
      </Flex>
    </Flex>
  )
}
